import React, { useEffect } from 'react';
import { APITypes } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import TempImagePicker, {
    TempAndPersistedAvatarState,
    useTempAndExistingAvatarState,
} from '../../../../v2BaseComponents/ImagePicker/TempImagePicker';
import TempFilePicker, { TempFileValue } from '../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import { AttachmentType } from '../../utils/types';
import { PromptActions, StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import Stack, { ContentStack, contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { Input } from '../../../../v2BaseComponents/Inputs';
import Button from '../../../../v2BaseComponents/Button';

const intl = getIntl('attachment-picker', {
    addImageTitle: 'Attach Image',
    addFileTitle: 'Attach File',
    add: 'Attach to Note',
    image: 'Image',
    file: 'File',
    description: 'Description',
    cameraRoll: 'Choose from camera roll',
    camera: 'Take a photo',
});

export type { AttachmentType };

export type AttachmentPickerOwnProps = {
    isOpen: boolean;
    onCancel: () => void;
    onPick: (file: APITypes.notesService.NoteFileCreate) => void;
    attachmentType: AttachmentType;
};

export type AttachmentPickerProps = AttachmentPickerOwnProps;

const defaultAvatarState: TempAndPersistedAvatarState = {
    type: 'cleared',
};

const AttachedFilePicker: React.FC<{
    fileState: TempFileValue | null;
    onFileStateUpdate: (newFileState: TempFileValue | null) => void;
}> = ({ fileState, onFileStateUpdate }) => {
    return <TempFilePicker value={fileState} onChange={onFileStateUpdate} title={intl('file')} />;
};

const AttachedImagePicker: React.FC<{
    avatarState: TempAndPersistedAvatarState;
    onAvatarStateUpdate: (newAvatarState: TempAndPersistedAvatarState) => void;
}> = ({ avatarState, onAvatarStateUpdate }) => {
    const { tempImagePickerProps } = useTempAndExistingAvatarState({
        existingAvatarUrl: null,
        avatarState,
        onAvatarStateUpdate,
    });
    return (
        <TempImagePicker
            {...tempImagePickerProps}
            title={intl('image')}
            imagePickerOptions={{}}
            style={{ width: '100%' }}
            renderImageSourcePicker={(onPick) => {
                return (
                    <Stack vertical fullWidth justify="center" align="center" space={contentStackSpacing}>
                        <Button
                            variant="select"
                            icon="Camera"
                            onPress={() => onPick('camera')}
                            title={intl('camera')}
                            size="lg"
                        />
                        <Button
                            variant="select"
                            icon="Image"
                            onPress={() => onPick('cameraRoll')}
                            title={intl('cameraRoll')}
                            size="lg"
                        />
                    </Stack>
                );
            }}
        />
    );
};

const AttachmentPicker: React.FC<AttachmentPickerProps> = ({ isOpen, onCancel, onPick, attachmentType }) => {
    const [description, setDescription] = React.useState('');
    const [avatarState, setAvatarState] = React.useState<TempAndPersistedAvatarState>(defaultAvatarState);
    const [fileState, setFileState] = React.useState<TempFileValue | null>(null);
    useEffect(() => {
        setDescription('');
        setAvatarState(defaultAvatarState);
        setFileState(null);
    }, [isOpen]);

    return (
        <StandardPrompt
            isOpen={isOpen}
            setIsOpen={(isOpen) => !isOpen && onCancel()}
            title={attachmentType === 'image' ? intl('addImageTitle') : intl('addFileTitle')}
        >
            <ContentStack>
                {attachmentType === 'image' ? (
                    <AttachedImagePicker
                        avatarState={avatarState}
                        onAvatarStateUpdate={(imageState) => {
                            setAvatarState(imageState);

                            if (imageState?.type === 'updated' && !description) {
                                setDescription(intl('image'));
                            }
                        }}
                    />
                ) : (
                    <AttachedFilePicker
                        fileState={fileState}
                        onFileStateUpdate={(fileState) => {
                            setFileState(fileState);

                            if (fileState?.originalName && !description) {
                                setDescription(fileState.originalName);
                            }
                        }}
                    />
                )}
                <Input
                    value={description}
                    onChange={(e) => setDescription(e.nativeEvent.text)}
                    title={intl('description')}
                    testID="attachment-description"
                />
                <PromptActions
                    actions={[
                        {
                            title: intl('add'),
                            icon: 'PlusCircle',
                            onPress: () => {
                                if (attachmentType === 'image') {
                                    // should always be true, just keeping ts happy
                                    if (avatarState.type === 'updated') {
                                        onPick({
                                            description,
                                            type: 'create',
                                            tempFileId: avatarState.value.tempFileId,
                                            name: avatarState.value.name,
                                            contentType: avatarState.value.contentType,
                                        });
                                    }
                                } else {
                                    // should always be true, just keeping ts happy
                                    if (fileState) {
                                        onPick({
                                            description,
                                            type: 'create',
                                            tempFileId: fileState.tempFileId,
                                            name: fileState.name,
                                            contentType: fileState.contentType,
                                        });
                                    }
                                }
                            },
                            testID: 'attachment-picker-add-button',
                            isDisabled: !description || (attachmentType === 'image' && avatarState.type !== 'updated'),
                        },
                    ]}
                />
            </ContentStack>
        </StandardPrompt>
    );
};

export default React.memo(AttachmentPicker);
